import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  AvailabilityFeedback,
  SubscriptionQueryResult,
  UserCancelSubscriptionMutationsArgs,
  UserSubscription,
  UserSubscriptionChannelQueryResult,
  UserSubscriptionQueriesFindAllArgs
} from '@interfaces/schema';

/**
 * Service of assistant subscription.
 */
@Injectable()
export class AssistantSubscriptionService {
  private readonly apollo = inject(Apollo);

  /**
   * Fetch subscription products.
   *
   * @returns {Observable<SubscriptionQueryResult>}
   */
  fetchSubscriptionProducts(): Observable<SubscriptionQueryResult> {
    const query = gql`
      query getSubscriptionList {
        Subscription {
          findAll {
            count
            results {
              id
              code
              type
              showOnShop
              showOnInternationalShop
              price
              chatQuota
              duration
              createdAt
              updatedAt
              title
              description
            }
          }
        }
      }
    `;

    return this.apollo
      .query({
        query
      })
      .pipe(
        map(
          (response) =>
            (response.data as any)?.Subscription
              ?.findAll as SubscriptionQueryResult
        )
      );
  }

  /**
   * Fetch user subscription channels.
   *
   * @param {UserSubscriptionQueriesFindAllArgs} variables Variables.
   * @returns {Observable<UserSubscriptionChannelQueryResult>}
   */
  fetchUserSubscriptionChannels(
    variables: UserSubscriptionQueriesFindAllArgs
  ): Observable<UserSubscriptionChannelQueryResult> {
    const query = gql`
      query userChatSubscription {
        User {
          Subscription {
            UserSubscriptionChannel {
              findAll {
                count
                results {
                  id
                  profileId
                  firstMessageAt
                  dailyChatEligbileFromAt
                  dailyChatEligbileUntilAt
                  paymentStatus
                  baseDailyChatQuota
                  paymentMethod
                  type
                  userSubscriptionId
                  subscriptionId
                  orderId
                  chatQuota
                  chatQuotaLeft
                  expiredAt
                  dailyChatQuota
                  status
                  createdAt
                  updatedAt
                }
              }
            }
          }
        }
      }
    `;

    return this.apollo
      .query({
        query,
        variables
      })
      .pipe(
        map(
          (response) =>
            (response.data as any)?.User?.Subscription?.UserSubscriptionChannel
              ?.findAll as UserSubscriptionChannelQueryResult
        )
      );
  }

  /**
   * Listen availability feedback form.
   *
   * @returns {Observable<Array<AvailabilityFeedback>>}
   */
  checkAllAvailability(): Observable<Array<AvailabilityFeedback>> {
    const query = gql`
      query CheckAllAvailibility {
        User {
          FeedbackResponse {
            checkAllAvailability {
              id
              code
              title
              description
              ratingEnabled
              status
              choices {
                name
                code
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query({
        query
      })
      .pipe(
        map(
          (response) =>
            (response.data as any).User?.FeedbackResponse
              ?.checkAllAvailability as Array<AvailabilityFeedback>
        )
      );
  }

  /**
   * @description Cancel subscription order mutation.
   * @param {UserCancelSubscriptionMutationsArgs} [variables] Params.
   * @param {UserCancelSubscriptionMutationsArgs} variables.form Params.
   * @returns {UserSubscription}
   * @memberof AssistantSubscriptionService
   */
  saveFeedback(variables?: {
    form: UserCancelSubscriptionMutationsArgs;
  }): Observable<UserSubscription> {
    const mutation = gql`
      mutation saveFeedback($form: UserFeedbackForm!) {
        User {
          FeedbackResponse {
            save(form: $form) {
              id
              profileId
              formId
              name
              externalId
              choice
              rating
              message
              submittedAt
            }
          }
        }
      }
    `;
    return this.apollo
      .mutate<any>({ mutation, variables, fetchPolicy: 'no-cache' })
      .pipe(
        map(
          ({ data }) => data?.User?.FeedbackResponse?.save as UserSubscription
        )
      );
  }
}
